import { UseFormSetValue } from 'react-hook-form';
import { memo, useEffect, useMemo, useState } from 'react';
import { RoleConfig, UserRole } from '../../../../../types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Visibility from '@mui/icons-material/Visibility';
interface RoleAccordianSectionProps {
  controlName:
    | 'userViewRoles'
    | 'userRoles'
    | 'directoryRoles'
    | 'gPanelContactsRoles'
    | 'calendarRoles'
    | 'driveRoles'
    | 'toolsRoles'
    | 'administrationRoles'
    | 'billingRoles'
    | 'rulesEngineRoles';
  title: string;
  setValue: UseFormSetValue<UserRole>;
  role: UserRole;
  formPermissionsValues: string[];
  allPermissionsValues: string[];
  immediateNotifications: string[];
  changeLogs: string[];
  roleType: RoleConfig;
  searchTerm: string;
  selectedPermissionType: string;
  openCloseAllAccordians: boolean;
}

const RoleAccordianSection = memo(
  ({
    controlName,
    title,
    setValue,
    role,
    formPermissionsValues,
    allPermissionsValues,
    immediateNotifications,
    changeLogs,
    roleType,
    searchTerm,
    selectedPermissionType,
    openCloseAllAccordians,
  }: RoleAccordianSectionProps) => {
    const [open, setOpen] = useState(true);

    // Memoize allPermissionsArray based on roleType
    const allPermissionsArray = useMemo(() => {
      return Object.keys(roleType).map(
        (key) => roleType[key as keyof typeof roleType]
      );
    }, [roleType]); // Dependency array here

    // Filter based on searchTerm
    const filteredPermissions = useMemo(() => {
      return allPermissionsArray.filter((permission) => {
        const matchesSearchTerm = permission.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const matchesType =
          selectedPermissionType !== 'all'
            ? permission.type === selectedPermissionType
            : true;
        return matchesSearchTerm && matchesType;
      });
    }, [allPermissionsArray, searchTerm, selectedPermissionType]);

    const isSpecificDeleteOverridden = useMemo(() => {
      const isDeleteAllChecked =
        formPermissionsValues.includes('deleteVaultExports');
      const specificDeleteRoles = [
        'deleteGmailVaultExport',
        'deleteChatVaultExport',
        'deleteGeminiVaultExport',
        'deleteCalendarVaultExport',
        'deleteDriveVaultExport',
        'deleteGroupsVaultExport',
      ];

      return (permissionValue: string) => {
        return (
          controlName === 'toolsRoles' &&
          specificDeleteRoles.includes(permissionValue) &&
          isDeleteAllChecked
        );
      };
    }, [controlName, formPermissionsValues]);

    // Memoize filteredPermissionsValues based on filteredPermissions
    const filteredPermissionsValues = useMemo(() => {
      return filteredPermissions.map((permission) => permission.value);
    }, [filteredPermissions]); // Dependency array here

    const changeLogPermissions = useMemo(() => {
      return filteredPermissions.filter(
        (permission) => permission.hasChangeLog
      );
    }, [filteredPermissions]); // Dependency array here

    // Memoize changeLogPermissionsValues based on changeLogRoles
    const changeLogPermissionsValues = useMemo(() => {
      return changeLogPermissions.map((permission) => permission.value);
    }, [changeLogPermissions]); // Dependency array here

    // Helper to check if a specific permission checkbox should be disabled and forced checked
    const isPermissionOverridden = useMemo(() => {
      const isViewAllVaultChecked =
        formPermissionsValues.includes('viewAllVault');
      const isDownloadAllChecked = formPermissionsValues.includes(
        'downloadVaultExport'
      );
      const specificDownloadRoles = [
        'downloadGmailVaultExport',
        'downloadChatVaultExport',
        'downloadGeminiVaultExport',
        'downloadCalendarVaultExport',
        'downloadDriveVaultExport',
        'downloadGroupsVaultExport',
      ];
      const viewVaultRolesOverridden = [
        'viewVaultMatters',
        'viewVaultExports',
        'viewGmailVaultExport',
        'viewChatVaultExport',
        'viewGeminiVaultExport',
        'viewCalendarVaultExport',
        'viewDriveVaultExport',
        'viewGroupsVaultExport',
      ];

      return (permissionValue: string) => {
        if (controlName !== 'toolsRoles') return false;

        if (
          viewVaultRolesOverridden.includes(permissionValue) &&
          isViewAllVaultChecked
        ) {
          return true;
        }

        if (
          specificDownloadRoles.includes(permissionValue) &&
          isDownloadAllChecked
        ) {
          return true;
        }

        return false;
      };
    }, [controlName, formPermissionsValues]);

    const isSpecificCreateOverridden = useMemo(() => {
      const isCreateAllChecked =
        formPermissionsValues.includes('createVaultExports');
      const specificCreateRoles = [
        'createGmailVaultExport',
        'createChatVaultExport',
        'createGeminiVaultExport',
        'createCalendarVaultExport',
        'createDriveVaultExport',
        'createGroupsVaultExport',
      ];

      return (permissionValue: string) => {
        return (
          controlName === 'toolsRoles' &&
          specificCreateRoles.includes(permissionValue) &&
          isCreateAllChecked
        );
      };
    }, [controlName, formPermissionsValues]);

    const isSpecificViewOverridden = useMemo(() => {
      const isViewAllChecked =
        formPermissionsValues.includes('viewVaultExports');
      const specificViewRoles = [
        'viewGmailVaultExport',
        'viewChatVaultExport',
        'viewGeminiVaultExport',
        'viewCalendarVaultExport',
        'viewDriveVaultExport',
        'viewGroupsVaultExport',
      ];

      return (permissionValue: string) => {
        return (
          controlName === 'toolsRoles' &&
          specificViewRoles.includes(permissionValue) &&
          isViewAllChecked
        );
      };
    }, [controlName, formPermissionsValues]);

    // Open accordions if the user is searching for a permission
    useEffect(() => {
      if (searchTerm.length > 0) {
        setOpen(true);
      }
    }, [searchTerm]);

    // Open/Close all accordions if the user clicks the collapse/expand all button
    useEffect(() => {
      setOpen(openCloseAllAccordians);
    }, [openCloseAllAccordians]);

    if (filteredPermissions.length === 0) {
      return null;
    }

    return (
      <>
        {controlName === 'userViewRoles' && role.roleType === 'user' ? (
          <Accordion
            expanded={open}
            onChange={() => {
              if (allPermissionsValues.length === 0) return;
              setOpen(!open);
            }}
          >
            <AccordionSummary
              expandIcon={
                <IconButton
                  component="span"
                  disabled={allPermissionsValues.length === 0}
                  sx={{ mr: -1 }}
                  disableRipple
                  tabIndex={-1}
                >
                  <ExpandMore />
                </IconButton>
              }
              aria-controls={`${controlName}-content`}
              id={`${controlName}}-header`}
            >
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                  {title}
                </Typography>
                {allPermissionsValues.length > 0 ? (
                  <Tooltip
                    arrow
                    title={`${
                      formPermissionsValues.filter((value) =>
                        allPermissionsValues.includes(value)
                      ).length
                    }/${
                      allPermissionsValues.length
                    } active permissions for ${title}.`}
                  >
                    <Chip
                      label={`${
                        formPermissionsValues.filter((value) =>
                          allPermissionsValues.includes(value)
                        ).length
                      }/${allPermissionsValues.length}`}
                      size="small"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                sx={{
                  px: 4,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Permission Name
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Active
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
                          Select/Deselect All
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={filteredPermissionsValues.every((value) =>
                            formPermissionsValues.includes(value)
                          )}
                          onChange={() => {
                            if (
                              filteredPermissionsValues.every((value) =>
                                formPermissionsValues.includes(value)
                              )
                            ) {
                              setValue(
                                `${controlName}`,
                                formPermissionsValues.filter(
                                  (permission) =>
                                    !filteredPermissionsValues.includes(
                                      permission
                                    )
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...formPermissionsValues,
                                  ...filteredPermissionsValues,
                                ]),
                              ];
                              setValue(`${controlName}`, combinedPermissions, {
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPermissions.map((currentPermission) => (
                      <TableRow key={currentPermission.value}>
                        <TableCell padding="checkbox">
                          <Stack
                            flexDirection={'row'}
                            alignItems="center"
                            gap={1}
                          >
                            {currentPermission.type === 'view' ? (
                              <Tooltip arrow title={'View'}>
                                <Visibility />
                              </Tooltip>
                            ) : currentPermission.type === 'update' ? (
                              <Tooltip arrow title={'Edit'}>
                                <Edit />
                              </Tooltip>
                            ) : currentPermission.type === 'delete' ? (
                              <Tooltip arrow title={'Delete'}>
                                <Delete />
                              </Tooltip>
                            ) : currentPermission.type === 'trash' ? (
                              <Tooltip arrow title={'Trash'}>
                                <Delete />
                              </Tooltip>
                            ) : (
                              <Tooltip arrow title={'Create'}>
                                <AddCircleOutline />
                              </Tooltip>
                            )}
                            <Typography>{currentPermission.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={role.default && role.roleType === 'admin'}
                            checked={formPermissionsValues.includes(
                              currentPermission.value
                            )}
                            onChange={() => {
                              if (
                                !formPermissionsValues.includes(
                                  currentPermission.value
                                )
                              ) {
                                setValue(
                                  `${controlName}`,
                                  [
                                    ...formPermissionsValues,
                                    currentPermission.value,
                                  ],
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                setValue(
                                  `${controlName}`,
                                  formPermissionsValues.filter(
                                    (value) => value !== currentPermission.value
                                  ),
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : controlName === 'userViewRoles' && role.roleType === 'admin' ? (
          <Accordion
            expanded={open}
            onChange={() => {
              if (allPermissionsValues.length === 0) return;
              setOpen(!open);
            }}
          >
            <AccordionSummary
              expandIcon={
                <IconButton
                  component="span"
                  disabled={allPermissionsValues.length === 0}
                  sx={{ mr: -1 }}
                  disableRipple
                  tabIndex={-1}
                >
                  <ExpandMore />
                </IconButton>
              }
              aria-controls={`${controlName}-content`}
              id={`${controlName}}-header`}
            >
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                  {title}
                </Typography>
                {allPermissionsValues.length > 0 ? (
                  <Tooltip
                    arrow
                    title={`${
                      formPermissionsValues.filter((value) =>
                        allPermissionsValues.includes(value)
                      ).length
                    }/${
                      allPermissionsValues.length
                    } active permissions for ${title}.`}
                  >
                    <Chip
                      label={`${
                        formPermissionsValues.filter((value) =>
                          allPermissionsValues.includes(value)
                        ).length
                      }/${allPermissionsValues.length}`}
                      size="small"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                sx={{
                  px: 4,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Permission Name
                        </Typography>
                      </TableCell>
                      <TableCell padding="none">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Active
                        </Typography>
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Immediate Notifications
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell padding="none">
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
                          Select/Deselect All
                        </Typography>
                      </TableCell>
                      <TableCell padding="none">
                        <Checkbox
                          checked={filteredPermissionsValues.every((value) =>
                            formPermissionsValues.includes(value)
                          )}
                          disabled={role.default && role.roleType === 'admin'}
                          onChange={() => {
                            if (
                              filteredPermissionsValues.every((value) =>
                                formPermissionsValues.includes(value)
                              )
                            ) {
                              setValue(
                                `${controlName}`,
                                formPermissionsValues.filter(
                                  (permission) =>
                                    !filteredPermissionsValues.includes(
                                      permission
                                    )
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...formPermissionsValues,
                                  ...filteredPermissionsValues,
                                ]),
                              ];
                              setValue(`${controlName}`, combinedPermissions, {
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Checkbox
                          checked={filteredPermissionsValues.every((value) =>
                            immediateNotifications.includes(value)
                          )}
                          onChange={() => {
                            if (
                              filteredPermissionsValues.every((value) =>
                                immediateNotifications.includes(value)
                              )
                            ) {
                              const filteredPermissions =
                                immediateNotifications.filter(
                                  (value) =>
                                    !filteredPermissionsValues.includes(value)
                                );
                              setValue(
                                'immediateNotification',
                                [...filteredPermissions],
                                { shouldDirty: true }
                              );
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...immediateNotifications,
                                  ...filteredPermissionsValues,
                                ]),
                              ];
                              setValue(
                                'immediateNotification',
                                combinedPermissions,
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPermissions.map((currentPermission) => (
                      <TableRow key={currentPermission.value}>
                        <TableCell padding="none">
                          <Stack
                            flexDirection={'row'}
                            alignItems="center"
                            gap={1}
                          >
                            {currentPermission.type === 'view' ? (
                              <Tooltip arrow title={'View'}>
                                <Visibility />
                              </Tooltip>
                            ) : currentPermission.type === 'update' ? (
                              <Tooltip arrow title={'Edit'}>
                                <Edit />
                              </Tooltip>
                            ) : currentPermission.type === 'delete' ? (
                              <Tooltip arrow title={'Delete'}>
                                <Delete />
                              </Tooltip>
                            ) : currentPermission.type === 'trash' ? (
                              <Tooltip arrow title={'Trash'}>
                                <Delete />
                              </Tooltip>
                            ) : (
                              <Tooltip arrow title={'Create'}>
                                <AddCircleOutline />
                              </Tooltip>
                            )}
                            <Typography>{currentPermission.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell padding="none">
                          <Checkbox
                            disabled={role.default && role.roleType === 'admin'}
                            checked={formPermissionsValues.includes(
                              currentPermission.value
                            )}
                            onChange={() => {
                              if (
                                !formPermissionsValues.includes(
                                  currentPermission.value
                                )
                              ) {
                                setValue(
                                  `${controlName}`,
                                  [
                                    ...formPermissionsValues,
                                    currentPermission.value,
                                  ],
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                setValue(
                                  `${controlName}`,
                                  formPermissionsValues.filter(
                                    (value) => value !== currentPermission.value
                                  ),
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <Checkbox
                            checked={immediateNotifications.includes(
                              currentPermission.value
                            )}
                            onChange={() => {
                              if (
                                !immediateNotifications.includes(
                                  currentPermission.value
                                )
                              ) {
                                setValue(
                                  'immediateNotification',
                                  [
                                    ...immediateNotifications,
                                    currentPermission.value,
                                  ],
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                setValue(
                                  'immediateNotification',
                                  immediateNotifications.filter(
                                    (value) => value !== currentPermission.value
                                  ),
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Accordion
            expanded={open}
            onChange={() => {
              if (allPermissionsValues.length === 0) return;
              setOpen(!open);
            }}
          >
            <AccordionSummary
              expandIcon={
                <IconButton
                  component="span"
                  disabled={allPermissionsValues.length === 0}
                  sx={{ mr: -1 }}
                  disableRipple
                  tabIndex={-1}
                >
                  <ExpandMore />
                </IconButton>
              }
              aria-controls={`${controlName}-content`}
              id={`${controlName}}-header`}
            >
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                  {title}
                </Typography>
                {allPermissionsValues.length > 0 ? (
                  <Tooltip
                    arrow
                    title={`${
                      formPermissionsValues.filter((value) =>
                        allPermissionsValues.includes(value)
                      ).length
                    }/${
                      allPermissionsValues.length
                    } active permissions for ${title}.`}
                  >
                    <Chip
                      label={`${
                        formPermissionsValues.filter((value) =>
                          allPermissionsValues.includes(value)
                        ).length
                      }/${allPermissionsValues.length}`}
                      size="small"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                sx={{
                  pb: 2,
                  px: 4,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Permission Name
                        </Typography>
                      </TableCell>
                      <TableCell padding="none">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Active
                        </Typography>
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Immediate Notifications
                        </Typography>
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          Change Logs
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell padding="none">
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
                          Select/Deselect All
                        </Typography>
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Checkbox
                          disabled={role.default && role.roleType === 'admin'}
                          checked={filteredPermissionsValues.every(
                            (value) =>
                              formPermissionsValues.includes(value) ||
                              isPermissionOverridden(value) ||
                              isSpecificCreateOverridden(value) ||
                              isSpecificViewOverridden(value)
                          )}
                          onChange={() => {
                            if (
                              filteredPermissionsValues.every(
                                (value) =>
                                  formPermissionsValues.includes(value) ||
                                  isPermissionOverridden(value) ||
                                  isSpecificCreateOverridden(value) ||
                                  isSpecificViewOverridden(value)
                              )
                            ) {
                              setValue(
                                `${controlName}`,
                                formPermissionsValues.filter(
                                  (permission) =>
                                    !filteredPermissionsValues.includes(
                                      permission
                                    )
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...formPermissionsValues,
                                  ...filteredPermissionsValues,
                                ]),
                              ];
                              setValue(`${controlName}`, combinedPermissions, {
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Checkbox
                          checked={filteredPermissionsValues.every((value) =>
                            immediateNotifications.includes(value)
                          )}
                          onChange={() => {
                            if (
                              filteredPermissionsValues.every((value) =>
                                immediateNotifications.includes(value)
                              )
                            ) {
                              const filteredPermissions =
                                immediateNotifications.filter(
                                  (value) =>
                                    !filteredPermissionsValues.includes(value)
                                );
                              setValue(
                                'immediateNotification',
                                [...filteredPermissions],
                                { shouldDirty: true }
                              );
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...immediateNotifications,
                                  ...filteredPermissionsValues,
                                ]),
                              ];
                              setValue(
                                'immediateNotification',
                                combinedPermissions,
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <Checkbox
                          checked={changeLogPermissionsValues.every((value) =>
                            changeLogs.includes(value)
                          )}
                          onChange={() => {
                            if (
                              changeLogPermissionsValues.every((value) =>
                                changeLogs.includes(value)
                              )
                            ) {
                              const filteredPermissions = changeLogs.filter(
                                (value) =>
                                  !changeLogPermissionsValues.includes(value)
                              );
                              setValue('changeLogs', [...filteredPermissions], {
                                shouldDirty: true,
                              });
                            } else {
                              const combinedPermissions = [
                                ...new Set([
                                  ...changeLogs,
                                  ...changeLogPermissionsValues,
                                ]),
                              ];
                              setValue('changeLogs', combinedPermissions, {
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPermissions.map((currentUserPermission) => (
                      <TableRow key={currentUserPermission.value}>
                        <TableCell padding="none">
                          <Stack
                            flexDirection={'row'}
                            alignItems="center"
                            gap={1}
                          >
                            {currentUserPermission.type === 'view' ? (
                              <Tooltip arrow title={'View'}>
                                <Visibility />
                              </Tooltip>
                            ) : currentUserPermission.type === 'update' ? (
                              <Tooltip arrow title={'Edit'}>
                                <Edit />
                              </Tooltip>
                            ) : currentUserPermission.type === 'delete' ? (
                              <Tooltip arrow title={'Delete'}>
                                <Delete />
                              </Tooltip>
                            ) : currentUserPermission.type === 'trash' ? (
                              <Tooltip arrow title={'Trash'}>
                                <Delete />
                              </Tooltip>
                            ) : (
                              <Tooltip arrow title={'Create'}>
                                <AddCircleOutline />
                              </Tooltip>
                            )}
                            <Typography>
                              {currentUserPermission.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <Checkbox
                            disabled={
                              (role.default && role.roleType === 'admin') ||
                              isPermissionOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificCreateOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificViewOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificDeleteOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificCreateOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificViewOverridden(
                                currentUserPermission.value
                              )
                            }
                            checked={
                              formPermissionsValues.includes(
                                currentUserPermission.value
                              ) ||
                              isPermissionOverridden(
                                currentUserPermission.value
                              ) ||
                              formPermissionsValues.includes(
                                currentUserPermission.value
                              ) ||
                              isSpecificCreateOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificViewOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificDeleteOverridden(
                                currentUserPermission.value
                              ) ||
                              formPermissionsValues.includes(
                                currentUserPermission.value
                              ) ||
                              isSpecificCreateOverridden(
                                currentUserPermission.value
                              ) ||
                              isSpecificViewOverridden(
                                currentUserPermission.value
                              )
                            }
                            onChange={() => {
                              if (
                                !formPermissionsValues.includes(
                                  currentUserPermission.value
                                )
                              ) {
                                setValue(
                                  `${controlName}`,
                                  [
                                    ...formPermissionsValues,
                                    currentUserPermission.value,
                                  ],
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                setValue(
                                  `${controlName}`,
                                  formPermissionsValues.filter(
                                    (value) =>
                                      value !== currentUserPermission.value
                                  ),
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <Checkbox
                            checked={immediateNotifications.includes(
                              currentUserPermission.value
                            )}
                            onChange={() => {
                              if (
                                !immediateNotifications.includes(
                                  currentUserPermission.value
                                )
                              ) {
                                setValue(
                                  'immediateNotification',
                                  [
                                    ...immediateNotifications,
                                    currentUserPermission.value,
                                  ],
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                setValue(
                                  'immediateNotification',
                                  immediateNotifications.filter(
                                    (value) =>
                                      value !== currentUserPermission.value
                                  ),
                                  {
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          />
                        </TableCell>
                        {currentUserPermission.hasChangeLog ? (
                          <TableCell padding="none" align="center">
                            <Checkbox
                              checked={changeLogs.includes(
                                currentUserPermission.value
                              )}
                              onChange={() => {
                                if (
                                  !changeLogs.includes(
                                    currentUserPermission.value
                                  )
                                ) {
                                  setValue(
                                    'changeLogs',
                                    [
                                      ...changeLogs,
                                      currentUserPermission.value,
                                    ],
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                } else {
                                  setValue(
                                    'changeLogs',
                                    changeLogs.filter(
                                      (value) =>
                                        value !== currentUserPermission.value
                                    ),
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                }
                              }}
                            />
                          </TableCell>
                        ) : (
                          <TableCell
                            padding="none"
                            align="center"
                            style={{ visibility: 'hidden' }}
                          >
                            {/* Placeholder for alignment */}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </>
    );
  }
);

export default RoleAccordianSection;
